import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import swal from 'sweetalert';
import * as Yup from 'yup';
import { Link, RouteComponentProps, useHistory } from 'react-router-dom';
import { getSSOClientById, editSSOClient } from '../../data/sso-clients';
import Loading from 'components/Loading';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Nombre requerido'),
    slug: Yup.string().required('Slug requerido').matches(/^[a-z0-9-]+$/, 'Solo letras minúsculas, números y guiones'),
    issuer_url: Yup.string().required('Issuer URL requerido'),
    client_id: Yup.string().required('Client ID requerido'),
});

type Props = { id: string };

const EditSSOClient = ({ match }: RouteComponentProps<Props>) => {
    const history = useHistory();
    const { id } = match.params;
    const [loading, setLoading] = useState(true);

    const formik = useFormik({
        initialValues: {
            name: '',
            slug: '',
            issuer_url: '',
            client_id: '',
            client_secret: '',
            client_secret_hint: '',
            is_active: true,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            try {
                const payload: any = { ...values };
                if (!payload.client_secret) delete payload.client_secret;
                delete payload.client_secret_hint;
                await editSSOClient(id, payload);
                await swal('Afinidata', 'Cliente SSO actualizado correctamente', 'success');
                history.push('/admin/sso-clients');
            } catch (err) {
                swal('Afinidata', 'Error al actualizar el cliente SSO', 'error');
            }
        },
    });

    useEffect(() => {
        let mounted = true;
        getSSOClientById(id)
            .then(resp => {
                if (mounted) {
                    formik.setValues({ ...resp.data, client_secret: '', client_secret_hint: resp.data.client_secret_hint || '' });
                    setLoading(false);
                }
            })
            .catch(err => { console.log(err); setLoading(false); });
        return () => { mounted = false; };
    }, [id]);

    if (loading) return <Loading />;

    const field = (name: string, label: string, type = 'text', placeholder = '') => (
        <div className="form-group">
            <label>{label}</label>
            <input
                type={type}
                name={name}
                className={`form-control ${(formik.errors as any)[name] && (formik.touched as any)[name] ? 'is-invalid' : ''}`}
                value={(formik.values as any)[name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={placeholder}
            />
            {(formik.errors as any)[name] && (formik.touched as any)[name] && (
                <div className="invalid-feedback">{(formik.errors as any)[name]}</div>
            )}
        </div>
    );

    return (
        <div className="row">
            <div className="col-md-8 mx-auto">
                <div className="card">
                    <div className="card-header">
                        <h4>Editar cliente SSO</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={formik.handleSubmit}>
                            {field('name', 'Nombre')}
                            {field('slug', 'Slug')}
                            {field('issuer_url', 'Issuer URL')}
                            {field('client_id', 'Client ID')}
                            <div className="form-group">
                                <label>Client Secret (dejar vacío para no cambiar)</label>
                                <input
                                    type="password"
                                    name="client_secret"
                                    className="form-control"
                                    value={formik.values.client_secret}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.values.client_secret === '' && (formik.values as any).client_secret_hint && (
                                    <small className="text-success">
                                        <i className="fa fa-check-circle"></i> Secret configurado: ***{(formik.values as any).client_secret_hint}
                                    </small>
                                )}
                            </div>
                            <div className="form-group form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="is_active"
                                    name="is_active"
                                    checked={formik.values.is_active}
                                    onChange={formik.handleChange}
                                />
                                <label className="form-check-label" htmlFor="is_active">Activo</label>
                            </div>
                            <div className="form-group">
                                <button className="btn btn-primary" type="submit">
                                    <i className="fa fa-save"></i> Guardar
                                </button>
                                <Link to="/admin/sso-clients" className="btn btn-secondary ml-2">
                                    <i className="fa fa-arrow-left"></i> Cancelar
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditSSOClient;
